import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useUpgradeModal,
  FEATURE_NAMES,
  FEATURE_DESCRIPTIONS,
  FeatureKey,
} from '@/contexts/UpgradeModalContext';
import {
  PLAN_PRICING,
  PLAN_FEATURES,
  PLAN_DESCRIPTIONS,
  PLAN_LABELS,
  SubscriptionPlan,
} from '@/services/billing';
import { useSubscription } from '@/hooks/useSubscription';
import EmbeddedCheckout from './EmbeddedCheckout';
import { Lock, Rocket, Sparkles, Crown, Zap, Clock } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
// P0-1 — Orbit Business is not on sale for the 2026-08-28 launch. It
// must never appear as a purchasable row here, and a feature whose
// required plan IS Business has to say so honestly rather than
// rendering an upgrade ladder with nothing on it.
import { BUSINESS_ON_SALE, BUSINESS_HELD_MESSAGE } from '@/config/launch';

// PLANS-1B — 3-tier lineup.
const PLAN_ICONS: Record<SubscriptionPlan, React.ReactNode> = {
  free: <OrbitIcon name="lock.fill" fallback={Lock} className="h-5 w-5" />,
  orbit_starter: <OrbitIcon name="sparkles" fallback={Sparkles} className="h-5 w-5" />,
  orbit: <OrbitIcon name="airplane.departure" fallback={Rocket} className="h-5 w-5" />,
  orbit_business: <OrbitIcon name="crown.fill" fallback={Crown} className="h-5 w-5" />,
};

// BRAND-1 — the `orbit` tier badge was orange-500. Retoning it alone
// would have collided with the stock blue-500 on `orbit_starter`, so
// the whole ladder moved to the space-race accents and now climbs in
// one direction: quiet neutral → Instrument Teal (secondary) → Mission
// Blue (the brand tier, and the plan literally named Orbit) → Kapton
// Gold (top tier, the foil). Four badges that are never seen side by
// side still need to be told apart from memory, which is why each step
// is a different accent rather than a different weight of one.
const PLAN_COLORS: Record<SubscriptionPlan, string> = {
  free: 'bg-muted text-muted-foreground',
  orbit_starter: 'bg-orbit-teal-fill/20 text-orbit-teal border-orbit-teal/30',
  orbit: 'bg-orbit-blue-fill/20 text-orbit-blue border-orbit-blue/30',
  orbit_business: 'bg-orbit-gold-fill/20 text-orbit-gold border-orbit-gold/30',
};

type UpgradePlan = 'orbit_starter' | 'orbit' | 'orbit_business';

export const UpgradeModal: React.FC = () => {
  const { state, closeUpgradeModal } = useUpgradeModal();
  const { plan: currentPlan } = useSubscription();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<UpgradePlan | null>(null);
  const [selectedInterval] = useState<'monthly' | 'yearly'>('monthly');

  const { isOpen, feature, requiredPlan } = state;

  if (!feature || !requiredPlan) return null;

  const featureName = FEATURE_NAMES[feature];
  const featureDescription = FEATURE_DESCRIPTIONS[feature];
  const planFeatures = PLAN_FEATURES[requiredPlan];

  const handleUpgrade = (plan: UpgradePlan) => {
    setSelectedPlan(plan);
    setCheckoutOpen(true);
  };

  const handleCheckoutSuccess = () => {
    setCheckoutOpen(false);
    closeUpgradeModal();
  };

  // Get plans to show (required plan and higher)
  const planOrder: SubscriptionPlan[] = ['free', 'orbit_starter', 'orbit', 'orbit_business'];
  const requiredIndex = planOrder.indexOf(requiredPlan);
  const availablePlans = planOrder
    .slice(requiredIndex)
    .filter((p) => p !== 'free')
    // P0-1 — Business is not purchasable until it goes on sale with
    // video. Dropping it here also removes it as the "or go higher"
    // row underneath Starter/Orbit prompts, where it would have been
    // a live Upgrade button straight into a 503.
    .filter((p) => p !== 'orbit_business' || BUSINESS_ON_SALE) as UpgradePlan[];

  // P0-1 — the feature the studio just tapped lives on a tier that
  // isn't on sale (`videoOriginals`, `prioritySupport`,
  // `teamManagement`, `apiAccess`, `productionManagement`,
  // `creatorAccounts`, `intakeFormAI` all require Business). There is
  // no honest ladder to draw, so we say the true thing and give them
  // nothing to click.
  const heldBehindBusiness = availablePlans.length === 0;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && closeUpgradeModal()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader className="text-center pb-2">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <OrbitIcon name="bolt.fill" fallback={Zap} className="h-7 w-7" />
            </div>
            <DialogTitle className="font-editorial text-2xl md:text-3xl leading-none">
              {heldBehindBusiness ? 'Not quite yet.' : 'A little more room here.'}
            </DialogTitle>
            <DialogDescription className="text-base">
              <span className="font-medium text-foreground">{featureName}</span>{' '}
              {heldBehindBusiness
                ? 'lives on Orbit Business.'
                : 'lives on a bigger plan.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Feature being requested */}
            <div className="rounded-lg border border-border/50 bg-muted/30 p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <OrbitIcon name="lock.fill" fallback={Lock} className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">{featureName}</p>
                  <p className="text-sm text-muted-foreground">{featureDescription}</p>
                </div>
              </div>
            </div>

            {/* Current plan indicator */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Your current plan:</span>
              <Badge variant="outline" className={PLAN_COLORS[currentPlan]}>
                {PLAN_ICONS[currentPlan]}
                <span className="ml-1.5">{PLAN_LABELS[currentPlan]}</span>
              </Badge>
            </div>

            {/* P0-1 — held-tier branch. No price, no button, no
                "notify me" that nothing is wired to. One sentence and
                a close. */}
            {heldBehindBusiness && (
              <div className="rounded-lg border border-border/50 bg-muted/30 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <OrbitIcon name="clock" fallback={Clock} className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">Orbit Business isn't on sale yet</p>
                    <p className="text-sm text-muted-foreground">
                      {BUSINESS_HELD_MESSAGE} Nothing you've set up
                      changes in the meantime.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Available upgrade options */}
            {!heldBehindBusiness && (
            <div className="space-y-3">
              {availablePlans.map((plan) => {
                const price = PLAN_PRICING[plan];
                const isRecommended = plan === requiredPlan;

                return (
                  <div
                    key={plan}
                    className={`relative rounded-lg border p-4 transition-all ${
                      isRecommended
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                        : 'border-border/50 hover:border-border'
                    }`}
                  >
                    {isRecommended && (
                      <Badge className="absolute -top-2.5 left-4 bg-primary text-primary-foreground">
                        Recommended
                      </Badge>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${PLAN_COLORS[plan]}`}>
                          {PLAN_ICONS[plan]}
                        </div>
                        <div>
                          <p className="font-semibold">{PLAN_LABELS[plan]}</p>
                          <p className="text-sm text-muted-foreground">
                            ${price.monthly}/month
                            {price.yearly && (
                              <span className="ml-1 text-xs">
                                or ${price.yearly}/year
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <Button
                        size="sm"
                        variant={isRecommended ? 'default' : 'outline'}
                        onClick={() => handleUpgrade(plan)}
                      >
                        Upgrade
                      </Button>
                    </div>

                    <p className="mt-2 text-xs text-muted-foreground">
                      {PLAN_DESCRIPTIONS[plan]}
                    </p>
                  </div>
                );
              })}
            </div>
            )}

            {/* Key features of recommended plan */}
            {!heldBehindBusiness && (
            <div className="rounded-lg bg-muted/30 p-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                What you get with {PLAN_LABELS[requiredPlan]}
              </p>
              <ul className="grid gap-1.5 text-sm">
                {planFeatures.slice(0, 4).map((feature, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary/20 text-primary text-xs">✓</span>
                    {feature}
                  </li>
                ))}
                {planFeatures.length > 4 && (
                  <li className="text-muted-foreground">
                    +{planFeatures.length - 4} more features
                  </li>
                )}
              </ul>
            </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Embedded Checkout */}
      {selectedPlan && (
        <EmbeddedCheckout
          open={checkoutOpen}
          onOpenChange={setCheckoutOpen}
          plan={selectedPlan}
          interval={selectedInterval}
          onSuccess={handleCheckoutSuccess}
        />
      )}
    </>
  );
};
